import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import OnlyFansAdStripe from "../components/OnlyFansAdStripe";
import "../styles/coaching.css";
import { useAuth } from "../contexts/AuthContext";
import { Pin, Lock, Eye, MessageSquare, Plus, Flame } from "lucide-react";

export default function ForumView() {
  const { t, i18n } = useTranslation();
  const { forumId } = useParams();
  const { user, linkedCouple, postingAsCoupleId } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [trending, setTrending] = useState(new Set());
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Oversettelses-fallback for subforum-navn/beskrivelse (matcher Forum.jsx-logikken)
  const fname = (f) => (f && i18n.exists(`forum.sub.${f.slug}`)) ? t(`forum.sub.${f.slug}`) : f?.name;
  const fdesc = (f) => (f && i18n.exists(`forum.sub_desc.${f.slug}`)) ? t(`forum.sub_desc.${f.slug}`) : f?.description;

  useEffect(() => {
    api.get(`/forum/${forumId}/threads`).then((r) => setData(r.data));
    api.get(`/threads/trending`).then((r) => setTrending(new Set(r.data || []))).catch(() => {});
  }, [forumId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!user || user === false) return navigate("/login", { state: { from: `/forum/${forumId}` } });
    setLoading(true);
    try {
      const payload = { forum_id: forumId, title, body };
      if (postingAsCoupleId) payload.as_couple_id = postingAsCoupleId;
      const { data: created } = await api.post("/threads", payload);
      navigate(`/thread/${created.thread.id}`);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const postingAsCouple = !!postingAsCoupleId && linkedCouple && postingAsCoupleId === linkedCouple.id;

  if (!data) return <Layout><div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div></Layout>;

  // Sorter: festet først, så siste aktivitet
  const sortedThreads = [...data.threads].sort((a, b) => {
    if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
    return new Date(b.last_post_at || b.created_at) - new Date(a.last_post_at || a.created_at);
  });

  return (
    <Layout>
      <div className="font-meta mb-4">
        <Link to="/forum" className="hover:text-[var(--cream)]">{t("forum.breadcrumb")}</Link>
        <span className="mx-2">/</span>
        <span className="text-[var(--cream)]">{fname(data.forum)}</span>
      </div>

      <section
        className="border border-white/10 rounded-sm overflow-hidden mb-10"
        style={{ background: "rgba(255, 255, 255, 0.025)" }}
        data-testid="forum-panel"
      >
        {/* Forum-overskrift – mørkere grå transparent (matcher Forum.jsx) */}
        <header
          className="flex items-start justify-between gap-4 px-6 py-6 border-b border-white/10"
          style={{
            borderLeft: `4px solid var(--cognac)`,
            background: "rgba(255, 255, 255, 0.045)",
          }}
        >
          <div className="flex-1 min-w-0">
            <h1 className="font-fraunces italic text-3xl sm:text-4xl text-[var(--cream)] leading-tight mb-2">
              {fname(data.forum)}
            </h1>
            <p className="font-manrope text-[var(--cream-soft)] text-sm max-w-xl leading-relaxed">
              {fdesc(data.forum)}
            </p>
          </div>
          <button onClick={() => setShowNew((s) => !s)} className="btn-magenta shrink-0" data-testid="new-thread-button">
            <Plus className="h-3.5 w-3.5 inline mr-2" /> {t("forum.new_thread")}
          </button>
        </header>

        {showNew && (
          <form onSubmit={submit} className="p-6 border-b border-white/10 space-y-5" data-testid="new-thread-form"
                style={{ background: "rgba(255, 255, 255, 0.015)" }}>
            {postingAsCouple && (
              <div className="bg-[var(--magenta)]/[0.08] border-l-2 border-[var(--magenta)] px-3 py-2 flex items-center gap-2 text-xs font-manrope" data-testid="ctx-banner-couple">
                <span className="font-meta text-[var(--magenta)]">POSTER SOM PARET</span>
                <span className="text-[var(--cream)]">· {linkedCouple.name}</span>
              </div>
            )}
            <div>
              <div className="font-meta mb-2">{t("forum.title_label")}</div>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                     className="input-editorial" required minLength={3} maxLength={200}
                     data-testid="new-thread-title" />
            </div>
            <div>
              <div className="font-meta mb-2">{t("forum.content_label")} <span className="normal-case tracking-normal text-[10px] text-[var(--cream-faint)]">{t("forum.markdown_hint")}</span></div>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
                        className="input-editorial" required minLength={1}
                        placeholder={t("forum.body_placeholder")}
                        data-testid="new-thread-body" />
            </div>
            {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3">{error}</div>}
            <div className="flex gap-3">
              <button type="submit" disabled={loading} className="btn-magenta" data-testid="new-thread-submit">
                {loading ? t("forum.publishing") : t("forum.publish")}
              </button>
              <button type="button" onClick={() => setShowNew(false)} className="btn-ghost">
                {t("common.cancel")}
              </button>
            </div>
          </form>
        )}

        <div data-testid="thread-list">
          {sortedThreads.length === 0 ? (
            <div className="text-center py-16 font-fraunces italic text-[var(--cream-soft)] text-lg">
              {t("forum.no_threads_yet")}
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {sortedThreads.map((th, idx) => {
                const isTrending = trending.has(th.id);
                const showAdAfter = idx === 4 && sortedThreads.length > 5;
                return (
                  <React.Fragment key={th.id}>
                  <li
                    className="thread-card"
                    style={{ background: idx % 2 === 1 ? "rgba(255, 255, 255, 0.015)" : "rgba(255, 255, 255, 0.025)" }}
                  >
                    <Link to={`/thread/${th.id}`} className="grid grid-cols-12 gap-4 px-6 py-5 items-center" data-testid={`thread-row-${th.id}`}>
                      <div className="col-span-12 sm:col-span-8">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {th.is_pinned && (
                            <span title={t("forum.pinned_by_mod_title")} className="inline-flex items-center gap-1 text-[var(--magenta)] font-meta">
                              <Pin className="h-3 w-3" /> {t("forum.pinned")}
                            </span>
                          )}
                          {th.is_locked && (
                            <span title={t("forum.locked_title")} className="inline-flex items-center gap-1 text-[var(--cream-faint)] font-meta">
                              <Lock className="h-3 w-3" /> {t("forum.locked")}
                            </span>
                          )}
                          {isTrending && (
                            <span title={t("forum.trending_title")} className="inline-flex items-center gap-1 text-[var(--magenta)] font-meta" data-testid={`trending-${th.id}`}>
                              <Flame className="h-3 w-3" /> {t("forum.trending_label")}
                            </span>
                          )}
                        </div>
                        <h3 className="font-fraunces italic text-xl text-[var(--cream)] leading-tight">{th.title}</h3>
                        <div className="font-meta mt-2">
                          {t("common.by").toUpperCase()} <span className="text-[var(--cream)]">{th.author?.username || t("common.unknown")}</span> · {formatRelative(th.created_at)}
                        </div>
                      </div>
                      <div className="hidden sm:block col-span-2 text-right font-manrope text-xs text-[var(--cream-soft)]">
                        <div className="inline-flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> {th.post_count}</div>
                        <div className="inline-flex items-center gap-1.5 ml-3"><Eye className="h-3 w-3" /> {th.view_count}</div>
                      </div>
                      <div className="hidden sm:block col-span-2 font-meta text-right">
                        {formatRelative(th.last_post_at)}
                      </div>
                    </Link>
                  </li>
                  {showAdAfter && <OnlyFansAdStripe variant="thread-row" />}
                  </React.Fragment>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </Layout>
  );
}
